import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { assets, view, i18next, dialogs } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import elementSettings from 'src/js/modules/element-settings';
import customUI from 'src/js/modules/custom-ui';
import focalPoint from 'src/js/modules/focal-point';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import contextMenu from 'src/js/modules/context-menu';
import UploadImageContainer from 'src/js/modules/common/containers/upload-image-container';
import ReplaceImageButton from 'src/js/modules/common/components/replace-image-button';
import imageTypes from 'src/js/modules/extensions/common/constants/image-types';
import trackMediaReplaceButtonClicked from '../trackers/track-media-replace-button-clicked';
import trackMediaCropButtonClicked from '../trackers/track-media-crop-button-clicked';

const { IconButton } = designSystem;
const { Context: ElementSettingsContext } = elementSettings;

const IMAGE_REPLACE_TRIGGER_ID = 'ws-media-image-replace-trigger';

const convertOperationsToChangedAttributes = (operations) =>
  (operations ?? []).map(([, args = []]) => [args[1], args[2]]);

function ImageActionsContainer({ mediaContainerElement, onCrop, onReplace }) {
  const {
    dialog,
    tabs: { activeId, scrollContainerRef },
    analyticsTraits: { blockId, blockVariationId },
    sharedData,
  } = useContext(ElementSettingsContext);

  const elementId = view.accessors.getLiveElementId(mediaContainerElement);
  const hasPlaceholder = mediaContainerElement.hasAttribute('placeholder');

  const handleCropSubmit = useCallback(
    ({ operations }) => {
      const changedAttributes = convertOperationsToChangedAttributes(operations);

      onCrop(changedAttributes);
      dialog.onReopen();
    },
    [onCrop, dialog]
  );

  const handleCropCancel = useCallback(() => {
    dialog.onReopen();
  }, [dialog]);

  const handleCrop = useCallback(async () => {
    trackMediaCropButtonClicked({
      tabId: activeId,
      blockId,
      blockVariationId,
    });

    await sharedData.onPersist();

    customUI.operations.show(customUI.customUiTypes.EDIT_INLINE_IMAGE_FOCAL_POINT, {
      elementId,
      onSubmit: handleCropSubmit,
      onCancel: handleCropCancel,
      preserveSharedData: true,
      shouldApplyChangesOnSubmit: false,
    });
  }, [
    activeId,
    blockId,
    blockVariationId,
    onCrop,
    sharedData,
    elementId,
    handleCropSubmit,
    handleCropCancel,
  ]);

  const handleImageReplace = useCallback(
    async ({ source }) => {
      try {
        // `mediaContainerElement` prop becomes stale after stock gallery closes,
        // so we need to get a fresh reference using elementId
        const element = view.accessors.getLiveElement(elementId);

        if (!element) {
          // eslint-disable-next-line no-console
          console.error('Element not found for image replace:', elementId);
          return;
        }

        const imageUrl = assets.helpers.addBaseHref(source);
        const imageSize = await assets.helpers.getImageSize(imageUrl);

        focalPoint.helpers.setCurrentImageUrl(element, source);

        const contentScale = focalPoint.helpers.computeAdjustedScale(element, {
          originalMediaBounds: imageSize,
          scale: focalPoint.constants.common.DEFAULT_SCALE,
        });
        focalPoint.helpers.setScale(element, contentScale);
        focalPoint.helpers.setPosition(
          element,
          focalPoint.constants.common.DEFAULT_POSITION_STRING_VALUE
        );

        element.removeAttribute('placeholder');

        onReplace({ source });
        dialog.onReopen();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to replace image:', error);

        dialog.onReopen();
      }
    },
    [elementId, onReplace, dialog]
  );

  const handleReplaceButtonClick = useCallback(() => {
    trackMediaReplaceButtonClicked({
      tabId: activeId,
      blockId,
      blockVariationId,
    });

    const scrollContainer = scrollContainerRef?.current;

    if (scrollContainer) {
      scrollContainer.addEventListener(
        'scroll',
        () => {
          contextMenu.operations.hideContextMenu();
        },
        { once: true }
      );
    }
  }, [activeId, blockId, blockVariationId, scrollContainerRef]);

  const handleTriggerUploadImageClick = useCallback(async () => {
    await sharedData.onPersist();
  }, [sharedData]);

  const handleTriggerBrowseStockPhotosClick = useCallback(async () => {
    await sharedData.onPersist();
  }, [sharedData]);

  const handlePhotoStockDialogCancel = useCallback(() => {
    // Delay reopening to avoid race with modal internal hide on overlay cancel.
    requestAnimationFrame(() => {
      dialog.onReopen();
    });
  }, [dialog]);

  const handleWrongFileType = useCallback(() => {
    dialogs.operations.show(dialogTypes.FILE_TYPE_ERROR_DIALOG, {
      onDismiss: () => dialog.onReopen(),
    });
  }, [dialog]);

  return (
    <div className="ws-image-actions">
      <UploadImageContainer onImageUploadEnd={handleImageReplace} preserveSharedData>
        {(uploadProps) => (
          <ReplaceImageButton
            {...uploadProps}
            id={IMAGE_REPLACE_TRIGGER_ID}
            elementId={elementId}
            label={hasPlaceholder ? i18next.t('Add image') : i18next.t('Replace image')}
            accept={imageTypes.IMAGE_ACCEPT_TYPE}
            onPhotoStockDialogCancel={handlePhotoStockDialogCancel}
            onClick={handleReplaceButtonClick}
            onTriggerUploadImageClick={handleTriggerUploadImageClick}
            onTriggerBrowseStockPhotosClick={handleTriggerBrowseStockPhotosClick}
            onWrongFileType={handleWrongFileType}
          />
        )}
      </UploadImageContainer>
      {!hasPlaceholder && (
        <IconButton
          glyph="crop"
          size="extra-large"
          label={i18next.t('Crop')}
          onClick={handleCrop}
        />
      )}
    </div>
  );
}

ImageActionsContainer.propTypes = {
  mediaContainerElement: PropTypes.oneOfType([PropTypes.instanceOf(Element), PropTypes.object])
    .isRequired,
  onCrop: PropTypes.func.isRequired,
  onReplace: PropTypes.func.isRequired,
};

export default ImageActionsContainer;
